// A reusable error boundary component that provides error handling, analytics tracking, and a fallback UI
import React, { ErrorInfo, ReactNode } from 'react'
import { AnalyticsEvent } from '@/lib/analytics/types'
// Import EVENTS from provider instead of directly from events
import { EVENTS } from '@/lib/analytics/provider'

interface ErrorBoundaryProps {
    children: ReactNode
    fallback?: ReactNode
    onError?: (error: Error, errorInfo: ErrorInfo) => void
    analytics?: {
        track: (event: AnalyticsEvent) => void
        events: typeof EVENTS
    }
}

interface ErrorBoundaryState {
    hasError: boolean
    error?: Error
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Track the error with analytics if available
        if (this.props.analytics) {
            this.props.analytics.track({
                name: this.props.analytics.events.ERROR_OCCURRED,
                properties: {
                    error_message: error.message,
                    error_name: error.name,
                    error_stack: error.stack,
                    component_stack: errorInfo.componentStack,
                    url: window.location.href,
                    path: window.location.pathname
                }
            })
        }

        // Call the onError handler if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo)
        }

        // Log the error to the console
        console.error("Uncaught error:", error, errorInfo)
    }

    render() {
        if (this.state.hasError) {
            // Return custom fallback UI or default error message
            return this.props.fallback || (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
                    <p className="text-gray-600 mb-4">
                        We apologize for the inconvenience. Please try refreshing the page.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                        Refresh Page
                    </button>
                    {this.state.error && (
                        <div className="mt-6 p-4 bg-gray-100 rounded-lg text-left text-sm text-gray-800 w-full max-w-xl">
                            <p className="font-medium">Error: {this.state.error.message}</p>
                        </div>
                    )}
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
